import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { OrderAPI } from '@certi/shared/api/order/OrderAPI'
import type { ShippingEstimateRequest, ShippingRateOption } from '@certi/shared/api/order/OrderAPI'
import { AddressAPI } from '@certi/shared/api/user/AddressAPI'
import type { UserOrderItemResponse } from '@certi/shared/types'
import { redirectToHostedCheckout } from '@/lib/checkout'

export default function CheckoutRedirectPage() {
  const { orderId } = useParams<{ orderId: string }>()
  const navigate = useNavigate()
  const { user, session } = useAuth()

  const [order, setOrder] = useState<UserOrderItemResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [redirecting, setRedirecting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [shippingError, setShippingError] = useState<string | null>(null)
  const [shippingLoading, setShippingLoading] = useState(false)
  const [shippingDirty, setShippingDirty] = useState(false)
  const [shippingRates, setShippingRates] = useState<ShippingRateOption[]>([])
  const [selectedShippingRate, setSelectedShippingRate] = useState<ShippingRateOption | null>(null)
  const [shippingForm, setShippingForm] = useState<ShippingEstimateRequest>({
    street: '',
    city: '',
    state: '',
    postalCode: '',
    country: 'US',
  })

  useEffect(() => {
    if (!user || !session?.accessToken || !orderId) {
      navigate('/login')
      return
    }

    let cancelled = false

    const loadCheckout = async () => {
      try {
        setLoading(true)
        setError(null)

        const orders = await OrderAPI.getUserAllOrders(session.accessToken)
        const foundOrder = orders.find(candidate => candidate.paymentId === orderId)

        if (!foundOrder) {
          if (!cancelled) {
            setError('Order not found for this account')
          }
          return
        }

        if (foundOrder.isPaid) {
          navigate(`/checkout/${orderId}/success`, { replace: true })
          return
        }

        if (!cancelled) {
          setOrder(foundOrder)
        }
      } catch (err) {
        console.error('Failed to load order:', err)
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Unable to load checkout')
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    void loadCheckout()

    return () => {
      cancelled = true
    }
  }, [navigate, orderId, session, user])

  useEffect(() => {
    if (!user?.id || !session?.accessToken) {
      return
    }

    const loadDefaultShippingAddress = async () => {
      try {
        const response = await AddressAPI.listAddresses(user.id, session.accessToken)
        const address = response.content.find(item => item.defaultShipping) ?? response.content[0]

        if (!address) {
          return
        }

        setShippingForm({
          street: address.street || '',
          city: address.city || '',
          state: address.state || '',
          postalCode: address.postalCode || '',
          country: address.country || 'US',
        })
        setShippingDirty(false)
      } catch (err) {
        console.warn('Default shipping address could not be loaded:', err)
      }
    }

    void loadDefaultShippingAddress()
  }, [user?.id, session?.accessToken])

  const handleShippingChange = (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = event.target
    setShippingForm(prev => ({
      ...prev,
      [name]: value,
    }))
    setShippingDirty(true)
  }

  const handleShippingEstimate = async () => {
    if (!order || !session?.accessToken) {
      return
    }

    if (!shippingForm.postalCode?.trim()) {
      setShippingError('Enter a ZIP or postal code')
      return
    }

    try {
      setShippingLoading(true)
      setShippingError(null)
      const buyerName = [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim()
      const response = await OrderAPI.estimateShipping(order.paymentId, {
        ...shippingForm,
        name: buyerName || user?.email,
        email: user?.email,
        phone: user?.phone,
        country: shippingForm.country || 'US',
        postalCode: shippingForm.postalCode.trim(),
      }, session.accessToken)

      setOrder(response.order)
      setShippingRates(response.rates)
      setSelectedShippingRate(response.selectedRate)
      setShippingDirty(false)
    } catch (err) {
      console.error('Failed to estimate shipping:', err)
      setShippingError(err instanceof Error ? err.message : 'Failed to estimate shipping')
    } finally {
      setShippingLoading(false)
    }
  }

  const handleContinueToStripe = async () => {
    if (!order || !session?.accessToken) {
      return
    }

    if (!order.shippoRateId || shippingDirty) {
      setShippingError('Calculate shipping before continuing')
      return
    }

    try {
      setRedirecting(true)
      setError(null)
      await redirectToHostedCheckout(order.paymentId, session.accessToken)
    } catch (err) {
      console.error('Failed to launch Stripe Checkout:', err)
      setError(err instanceof Error ? err.message : 'Unable to open Stripe Checkout')
      setRedirecting(false)
    }
  }

  const formatPrice = (amount: number, currency = 'USD') => new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  }).format(amount)

  const formatTransitTime = (rate: ShippingRateOption) => {
    if (rate.estimatedDays) {
      return `${rate.estimatedDays} business day${rate.estimatedDays === 1 ? '' : 's'}`
    }

    return rate.durationTerms || 'Delivery estimate unavailable'
  }

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16">
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary"></div>
        </div>
      </div>
    )
  }

  if (error || !order) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16">
        <div className="text-center py-12">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Unable To Open Stripe Checkout</h1>
          <p className="text-gray-600 mb-6">{error || 'Unable to load this order'}</p>
          <div className="flex flex-col sm:flex-row justify-center gap-3">
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="btn btn-primary"
            >
              Try Again
            </button>
            <button
              type="button"
              onClick={() => navigate('/profile/orders')}
              className="btn btn-secondary border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
            >
              View My Orders
            </button>
          </div>
        </div>
      </div>
    )
  }

  const shippingAmount = order.shippingAmount ?? 0
  const itemAmount = order.itemAmount ?? order.totalAmount - shippingAmount
  const hasShippingEstimate = Boolean(order.shippoRateId)
  const hasCurrentShippingEstimate = hasShippingEstimate && !shippingDirty
  const displayTotal = hasCurrentShippingEstimate ? order.totalAmount : itemAmount
  const appliedRate = selectedShippingRate
    ?? shippingRates.find(rate => rate.rateId === order.shippoRateId)
    ?? null
  const selectedShippingLabel = order.shippingProvider && order.shippingService
    ? `${order.shippingProvider} ${order.shippingService}`
    : 'Estimated shipping'

  return (
    <div className="max-w-3xl mx-auto px-4 py-10">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Complete Your Purchase</h1>
        <p className="text-gray-600 mt-2">
          Add delivery details before opening secure Stripe Checkout.
        </p>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Order Summary</h2>

        <div className="flex gap-4 mb-6">
          {order.listingImageUrl && (
            <img
              src={order.listingImageUrl}
              alt={order.listingTitle}
              className="w-24 h-24 object-cover rounded"
            />
          )}
          <div className="flex-1">
            <h3 className="font-semibold text-gray-900">{order.listingTitle}</h3>
            <p className="text-sm text-gray-600 mt-1">Auction ID: {order.auctionId}</p>
            <p className="text-sm text-gray-600">Order ID: {order.paymentId}</p>
          </div>
        </div>

        <div className="border-t border-gray-200 pt-4 space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Winning Bid</span>
            <span className="font-medium text-gray-900">{formatPrice(itemAmount)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Shipping</span>
            <span className="font-medium text-gray-900">
              {hasCurrentShippingEstimate ? formatPrice(shippingAmount) : 'Calculate before payment'}
            </span>
          </div>
          <div className="border-t border-gray-200 pt-2 flex justify-between">
            <span className="font-semibold text-gray-900">Total</span>
            <span className="font-bold text-gray-900 text-lg">{formatPrice(displayTotal)}</span>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Delivery</h2>
            {hasCurrentShippingEstimate && (
              <p className="text-sm text-gray-600 mt-1">
                {selectedShippingLabel}
                {order.shippingEstimatedDays ? `, estimated ${order.shippingEstimatedDays} days` : ''}
              </p>
            )}
            {hasCurrentShippingEstimate && order.shippingDestinationSummary && (
              <p className="text-sm text-gray-500 mt-1">
                {order.shippingDestinationSummary}
              </p>
            )}
          </div>
          {hasCurrentShippingEstimate && (
            <span className="text-sm font-semibold text-green-700 bg-green-50 border border-green-200 rounded-full px-3 py-1">
              {formatPrice(shippingAmount)}
            </span>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Street Address
            </label>
            <input
              type="text"
              name="street"
              value={shippingForm.street || ''}
              onChange={handleShippingChange}
              placeholder="Optional for ZIP estimate"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              City
            </label>
            <input
              type="text"
              name="city"
              value={shippingForm.city || ''}
              onChange={handleShippingChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              State
            </label>
            <input
              type="text"
              name="state"
              value={shippingForm.state || ''}
              onChange={handleShippingChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              ZIP / Postal Code<span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="postalCode"
              value={shippingForm.postalCode}
              onChange={handleShippingChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Country
            </label>
            <select
              name="country"
              value={shippingForm.country || 'US'}
              onChange={handleShippingChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="US">United States</option>
              <option value="CA">Canada</option>
              <option value="MX">Mexico</option>
              <option value="GB">United Kingdom</option>
              <option value="AU">Australia</option>
            </select>
          </div>
        </div>

        {shippingError && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600">{shippingError}</p>
          </div>
        )}

        {shippingDirty && hasShippingEstimate && (
          <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-sm text-yellow-800">Recalculate shipping to update the final total before payment.</p>
          </div>
        )}

        {shippingRates.length > 0 && !shippingDirty && (
          <div className="mt-5 rounded-lg border border-gray-200 overflow-hidden">
            <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
              <h3 className="font-semibold text-gray-900">Shipping Rates</h3>
              <p className="text-sm text-gray-600 mt-1">
                Lowest available rate is applied to this order.
              </p>
            </div>
            <div className="divide-y divide-gray-200">
              {shippingRates.slice(0, 5).map(rate => {
                const isAppliedRate = rate.rateId === (appliedRate?.rateId ?? order.shippoRateId)

                return (
                  <div
                    key={rate.rateId}
                    className={`flex items-center justify-between gap-4 px-4 py-3 ${
                      isAppliedRate ? 'bg-green-50' : 'bg-white'
                    }`}
                  >
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="font-medium text-gray-900">
                          {rate.provider} {rate.serviceLevelName}
                        </p>
                        {isAppliedRate && (
                          <span className="text-xs font-semibold text-green-700 bg-green-100 border border-green-200 rounded-full px-2 py-0.5">
                            Applied
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 mt-1">{formatTransitTime(rate)}</p>
                    </div>
                    <span className="shrink-0 font-semibold text-gray-900">
                      {formatPrice(rate.amount, rate.currency || 'USD')}
                    </span>
                  </div>
                )
              })}
            </div>
            {shippingRates.length > 5 && (
              <p className="bg-white px-4 py-3 text-sm text-gray-600">
                {shippingRates.length - 5} more carrier rate{shippingRates.length - 5 === 1 ? '' : 's'} available.
              </p>
            )}
          </div>
        )}

        <button
          type="button"
          onClick={handleShippingEstimate}
          disabled={shippingLoading || !shippingForm.postalCode?.trim()}
          className="mt-4 btn btn-secondary border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {shippingLoading ? 'Calculating...' : hasShippingEstimate ? 'Recalculate shipping' : 'Calculate shipping'}
        </button>
      </div>

      <button
        type="button"
        onClick={handleContinueToStripe}
        disabled={redirecting || !hasCurrentShippingEstimate}
        className="w-full btn btn-primary py-3 text-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {redirecting ? 'Opening Stripe...' : hasCurrentShippingEstimate ? 'Continue to Stripe Checkout' : 'Calculate shipping to continue'}
      </button>
    </div>
  )
}
